import type { ComponentProps, MouseEvent } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { FlexProps, HeightProps } from 'styled-system';
import { height, justifyContent, maxWidth } from 'styled-system';

import { useLocalization } from '../../providers/LocaleProvider';
import type { Space } from '../../theme/system-types';
import { Flex } from '../Flex';
import OtherVariations from './components/OtherVariations';
import SwatchPill, { type SwatchesContainer } from './components/SwatchPill';
import { useSwatchData } from './hooks/use-swatch-data';
import { useVariationDrawer } from './hooks/use-variation-drawer';
import type { ProductListVariation, TrackingContext } from './types/types';
import { groupOtherVariations } from './utils/utils';

interface VariationButtonProps {
  sku: string;
  variations: ProductListVariation[];
  productIndex: number;
  visibleProductsCount: number;
  mb?: Space;
  url: string;
  isHorizontalLayout?: boolean;
  trackingContext: TrackingContext;
  swatchContainerStyles?: Partial<ComponentProps<typeof SwatchesContainer>>;
}

const SwatchesFlexContainer = styled(Flex)<FlexProps & HeightProps>`
  ${justifyContent};
  ${height};
  ${maxWidth};
`;

const swatchesFlexContainerStyles: ComponentProps<typeof SwatchesFlexContainer> = {
  justifyContent: 'center',
  flexDirection: 'column',
  maxWidth: '166px',
};

const horizontalLayoutStyles: ComponentProps<typeof Flex> = {
  flexDirection: ['column', null, 'row'],
  alignItems: [null, null, 'center'],
  gap: ['sp_8', null, 'sp_16'],
};

const VariationButton = ({
  sku,
  variations,
  mb,
  url,
  isHorizontalLayout,
  trackingContext,
  swatchContainerStyles,
}: VariationButtonProps) => {
  const { r } = useLocalization();

  const { swatches, allSwatchesCount, visibleSwatchesCount, isMobile } = useSwatchData(variations);
  const otherVariations = groupOtherVariations(variations);
  const showOtherVariants = otherVariations.length > 0;

  const { handleClick, clickTrackingHandler } = useVariationDrawer({
    sku,
    url,
    variations,
    trackingContext,
  });

  return (
    <Flex {...(isHorizontalLayout ? horizontalLayoutStyles : { flexDirection: 'column' })} mb={mb}>
      <SwatchesFlexContainer
        {...swatchesFlexContainerStyles}
        mb={mb}
        onClick={(event: MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          handleClick();
          clickTrackingHandler(event);
        }}
        {...(swatches.length > 0 ? { height: 'sp_48' } : {})}
        data-testid="variation-button"
      >
        {swatches.length > 0 && (
          <SwatchPill
            swatches={swatches}
            allSwatchesCount={allSwatchesCount}
            visibleSwatchesCount={visibleSwatchesCount}
            isMobile={isMobile}
            ariaLabel={r.t('h24_product_count_color_material', { count: swatches.length })}
            swatchContainerStyles={{
              mt: 'sp_6',
              mb: 'sp_6',
              ...(showOtherVariants ? { mb: 'sp_4' } : {}),
              ...swatchContainerStyles,
            }}
          />
        )}
      </SwatchesFlexContainer>
      {showOtherVariants && (
        <OtherVariations
          otherVariations={otherVariations}
          clickTrackingHandler={clickTrackingHandler}
        />
      )}
    </Flex>
  );
};

export default VariationButton;
